import { nanoid } from 'nanoid'
import { SplineUtils } from '@/plugins/cad/module/utils'
import { EntityType } from '../../model/common/constant'
import {
  ArcEntity,
  type CircleEntity,
  type EllipseEntity,
  type IEntity,
  PolylineEntity,
  type RayEntity,
  type SplineEntity,
} from '../../model/entity'
import type { BreakResult } from '../EntityEditHandler'
import { BreakUtils } from './breakUtils'

/** 采样精度：将曲线型边界采样为折线段的分段数 */
const BOUNDARY_SAMPLE_NUM = 128
/** 样条曲线投影时的采样点数 */
const SPLINE_PROJECT_SAMPLE_NUM = 200
/** 射线采样长度（足够大以覆盖场景） */
const RAY_SAMPLE_LENGTH = 1e6
/** 浮点容差 */
const EPS = 1e-8
/** 角度容差（弧度） */
const ANGLE_EPS = 1e-6

type TrimInterval = { tLo: number; tHi: number; wrapping: boolean }

/**
 * 修剪工具几何计算类
 *
 * 核心算法：
 * 1. 将边界实体采样为折线段（世界坐标）
 * 2. 求折线段与目标实体的交点
 * 3. 将交点投影到目标实体的参数坐标系，得到交点参数值 t
 * 4. 对参数值排序后，找到包含光标位置 t_cursor 的区间 [tLo, tHi]
 * 5. 利用 BreakUtils.breakEntity 移除该区间
 */
export class TrimUtils {
  /** 支持修剪的实体类型（与打断工具相同） */
  static readonly EnabledEntityTypes = new Set<EntityType>([
    EntityType.Polyline,
    EntityType.Arc,
    EntityType.Circle,
    EntityType.Ellipse,
    EntityType.Spline,
    EntityType.Ray,
  ])

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * 修剪实体：移除与边界实体相交后，距光标最近一侧的片段
   * @param entity      要被修剪的目标实体
   * @param cursorPoint 鼠标光标世界坐标（决定哪一侧被修剪）
   * @param boundaries  作为剪切边的实体集合
   * @returns BreakResult（与 BreakUtils.breakEntity 返回结构相同）
   */
  static trimEntity(
    entity: IEntity,
    cursorPoint: { x: number; y: number },
    boundaries: IEntity[]
  ): BreakResult {
    const empty: BreakResult = { createdEntities: [], deleteEntities: [] }
    if (!TrimUtils.EnabledEntityTypes.has(entity.type)) {
      return empty
    }

    // 1. 求目标实体与所有边界的交点参数值
    const rawTs = TrimUtils.findIntersectionParams(entity, boundaries)
    // 无任何相交 → 删除整个实体
    if (rawTs.length === 0) {
      if (entity.type === EntityType.Polyline) {
        return TrimUtils.trimPolylineCursorSegment(entity as PolylineEntity, cursorPoint, [])
      }
      return { createdEntities: [], deleteEntities: [entity] }
    }

    // 2. 去重并排序
    const sorted = TrimUtils.deduplicateAndSort(rawTs)
    if (sorted.length === 0) return empty

    if (entity.type === EntityType.Polyline) {
      return TrimUtils.trimPolylineCursorSegment(entity as PolylineEntity, cursorPoint, sorted)
    }

    // 3. 将光标投影到目标实体，获取光标参数值
    const cursorT = TrimUtils.projectOnEntity(entity, cursorPoint)

    // 4. 判断目标实体是否为闭合形状
    const isClosed = TrimUtils.isEntityClosed(entity)
    const paramRange = TrimUtils.getParamRange(entity)

    // 5. 找到包含 cursorT 的修剪区间
    let interval: { tLo: number; tHi: number; wrapping: boolean } | null
    if (isClosed) {
      interval = TrimUtils.findIntervalClosed(
        sorted,
        cursorT,
        paramRange.period!,
        entity,
        cursorPoint
      )
    } else {
      interval = TrimUtils.findIntervalOpen(sorted, cursorT, paramRange.tMin, paramRange.tMax)
    }
    if (!interval) return empty

    // 6. 闭合实体直接保留修剪区间的互补段，避免跨参数起点时方向被误判
    if (isClosed) {
      if (entity.type === EntityType.Circle) {
        return TrimUtils.trimCircle(entity as CircleEntity, interval.tLo, interval.tHi)
      }
      if (entity.type === EntityType.Ellipse) {
        return TrimUtils.trimFullEllipse(entity as EllipseEntity, interval.tLo, interval.tHi)
      }
    }

    // 7. 将 [tLo, tHi] 转换为世界坐标点，调用 BreakUtils.breakEntity
    const p1 = TrimUtils.getPointAtParam(entity, interval.tLo)
    const p2 = TrimUtils.getPointAtParam(entity, interval.tHi)
    const res = BreakUtils.breakEntity(entity, { firstPoint: p1, secondPoint: p2 })
    if (res.createdEntities.length === 0 && res.deleteEntities.length === 0) {
      // 可能端点在边界上，导致没有实际被打断 → 删除整个实体
      res.deleteEntities = [entity]
    }
    return res
  }

  /**
   * 获取预览时应标记为"将被修剪"的实体片段。
   *
   * 执行结果中的 createdEntities 表示修剪后保留的片段；预览需要的是相反含义，
   * 即光标所在、即将被删除的那一段。
   */
  static getTrimmedPreviewEntities(
    entity: IEntity,
    cursorPoint: { x: number; y: number },
    boundaries: IEntity[]
  ): IEntity[] {
    if (!TrimUtils.EnabledEntityTypes.has(entity.type)) {
      return []
    }

    const rawTs = TrimUtils.findIntersectionParams(entity, boundaries)
    if (rawTs.length === 0 && entity.type !== EntityType.Polyline) {
      return [entity]
    }

    const sorted = TrimUtils.deduplicateAndSort(rawTs)
    const interval = TrimUtils.findTrimInterval(entity, cursorPoint, sorted)
    if (!interval || interval.tHi - interval.tLo < EPS) {
      return []
    }

    return TrimUtils.createTrimmedIntervalEntities(entity, interval)
  }

  /**
   * 将点投影到实体的参数坐标系，获取参数值 t（供 TrimEntityTool 预览使用）。
   *
   * - 开放折线：t ∈ [0, n-1]（n = 顶点数）
   * - 闭合折线：t ∈ [0, n]（n = 顶点数，含封口段）
   * - 圆弧：t = ccwSpan(startAngle, angle) ∈ [0, arcSpan]
   * - 圆：t = normalizedAngle ∈ [0, 2π)
   * - 椭圆弧：t = ccwSpan(startAngle, angle)
   * - 样条：t = u 参数 ∈ [uMin, uMax]
   *
   * @param entity - 目标实体
   * @param point - 待投影的点
   * @returns 实体参数坐标下的 t 值；不支持的类型返回 0
   */
  static projectOnEntity(entity: IEntity, point: { x: number; y: number }): number {
    switch (entity.type) {
      case EntityType.Polyline:
        return TrimUtils.projectOnPolyline(point, entity as PolylineEntity)
      case EntityType.Arc:
        return TrimUtils.projectOnArc(point, entity as ArcEntity)
      case EntityType.Circle:
        return TrimUtils.projectOnCircle(point, entity as CircleEntity)
      case EntityType.Ellipse:
        return TrimUtils.projectOnEllipse(point, entity as EllipseEntity)
      case EntityType.Spline:
        return TrimUtils.projectOnSpline(point, entity as SplineEntity)
      case EntityType.Ray:
        return TrimUtils.projectOnRay(point, entity as RayEntity)
      default:
        return 0
    }
  }

  // =========================================================================
  // 交点参数查找
  // =========================================================================

  /** 求目标实体与所有边界实体的交点参数值 */
  private static findIntersectionParams(entity: IEntity, boundaries: IEntity[]): number[] {
    const result: number[] = []
    for (const boundary of boundaries) {
      if (boundary.id === entity.id) continue // 跳过自身
      const pts = TrimUtils.findEntityBoundaryIntersections(entity, boundary)
      result.push(...pts)
    }
    return result
  }

  /** 求目标实体与单个边界实体的交点参数值 */
  private static findEntityBoundaryIntersections(entity: IEntity, boundary: IEntity): number[] {
    // 将边界采样为折线段
    const boundaryPts = TrimUtils.sampleEntityToWorldPoints(boundary)
    if (!boundaryPts || boundaryPts.length < 2) return []

    const result: number[] = []
    for (let i = 0; i < boundaryPts.length - 1; i++) {
      const p1 = boundaryPts[i]
      const p2 = boundaryPts[i + 1]
      // 求边界段与目标实体的交点（世界坐标）
      const intPts = TrimUtils.findSegmentEntityIntersectionPoints(p1, p2, entity)
      // 将交点投影到目标实体参数坐标系
      for (const pt of intPts) {
        result.push(TrimUtils.projectOnEntity(entity, pt))
      }
    }
    return result
  }

  // =========================================================================
  // 边界实体采样（世界坐标）
  // =========================================================================

  private static sampleEntityToWorldPoints(entity: IEntity): { x: number; y: number }[] | null {
    switch (entity.type) {
      case EntityType.Polyline:
        return TrimUtils.samplePolylineToPoints(entity as PolylineEntity)
      case EntityType.Arc:
        return TrimUtils.sampleArcToPoints(entity as ArcEntity, BOUNDARY_SAMPLE_NUM)
      case EntityType.Circle:
        return TrimUtils.sampleCircleToPoints(entity as CircleEntity, BOUNDARY_SAMPLE_NUM)
      case EntityType.Ellipse:
        return TrimUtils.sampleEllipseToPoints(entity as EllipseEntity, BOUNDARY_SAMPLE_NUM)
      case EntityType.Spline:
        return TrimUtils.sampleSplineToPoints(entity as SplineEntity, BOUNDARY_SAMPLE_NUM)
      case EntityType.Ray: {
        const { startPoint, unitVector } = entity as any
        return [
          { x: startPoint.x, y: startPoint.y },
          {
            x: startPoint.x + unitVector.x * RAY_SAMPLE_LENGTH,
            y: startPoint.y + unitVector.y * RAY_SAMPLE_LENGTH,
          },
        ]
      }
      default:
        return null
    }
  }

  private static samplePolylineToPoints(entity: PolylineEntity): { x: number; y: number }[] {
    const pts: { x: number; y: number }[] = []
    const vertices = entity.vertices
    const n = entity.isClosed ? vertices.length : vertices.length - 1
    for (let i = 0; i < n; i++) {
      const v1 = vertices[i]
      const v2 = vertices[(i + 1) % vertices.length]
      const bulge = v1.bulge ?? 0
      pts.push({ x: v1.x, y: v1.y })
      if (Math.abs(bulge) > 1e-10) {
        const arcPts = TrimUtils.sampleBulgeSegment(v1, v2, bulge, 16)
        pts.push(...arcPts.slice(1, -1))
      }
    }
    pts.push({
      x: vertices[n === vertices.length ? 0 : vertices.length - 1].x,
      y: vertices[n === vertices.length ? 0 : vertices.length - 1].y,
    })
    if (entity.isClosed && vertices.length > 0) {
      pts.push({ x: vertices[0].x, y: vertices[0].y })
    }
    return pts
  }

  private static sampleBulgeSegment(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    bulge: number,
    n: number
  ): { x: number; y: number }[] {
    const { cx, cy, radius, startAngle } = TrimUtils.arcParamsFromBulge(p1, p2, bulge)
    const sweep = 4 * Math.atan(bulge)
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= n; i++) {
      const angle = startAngle + (i / n) * sweep
      pts.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) })
    }
    return pts
  }

  private static sampleArcToPoints(entity: ArcEntity, n: number): { x: number; y: number }[] {
    const { center, radius, startAngle, endAngle, clockwise } = entity
    const span = clockwise
      ? -(TrimUtils.normalizeAngle(startAngle - endAngle) || Math.PI * 2)
      : TrimUtils.normalizeAngle(endAngle - startAngle) || Math.PI * 2
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= n; i++) {
      const angle = startAngle + (i / n) * span
      pts.push({ x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) })
    }
    return pts
  }

  private static sampleCircleToPoints(entity: CircleEntity, n: number): { x: number; y: number }[] {
    const { center, radius } = entity
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= n; i++) {
      const angle = (i / n) * Math.PI * 2
      pts.push({ x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) })
    }
    return pts
  }

  private static sampleEllipseToPoints(
    entity: EllipseEntity,
    n: number
  ): { x: number; y: number }[] {
    const { center, radiusX, radiusY, rotation, startAngle, endAngle } = entity
    let span = TrimUtils.ccwSpan(startAngle, endAngle)
    if (span < ANGLE_EPS) span = Math.PI * 2
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= n; i++) {
      const theta = startAngle + (i / n) * span
      const lx = radiusX * Math.cos(theta)
      const ly = radiusY * Math.sin(theta)
      pts.push({
        x: center.x + lx * Math.cos(rotation) - ly * Math.sin(rotation),
        y: center.y + lx * Math.sin(rotation) + ly * Math.cos(rotation),
      })
    }
    return pts
  }

  private static sampleSplineToPoints(entity: SplineEntity, n: number): { x: number; y: number }[] {
    if (entity.controlPoints.length < entity.degree + 1) return []
    const { knots, degree } = entity
    const uMin = knots[degree] ?? 0
    const uMax = (knots[knots.length - degree - 1] ?? 1) - 1e-9
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= n; i++) {
      const u = uMin + (uMax - uMin) * (i / n)
      const pt = SplineUtils.evaluateBSpline(u, entity.controlPoints, entity.knots, entity.degree)
      pts.push({ x: pt.x, y: pt.y })
    }
    return pts
  }

  // =========================================================================
  // 边界段与目标实体的交点（世界坐标）
  // =========================================================================

  private static findSegmentEntityIntersectionPoints(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    entity: IEntity
  ): { x: number; y: number }[] {
    switch (entity.type) {
      case EntityType.Polyline:
        return TrimUtils.segmentVsPolyline(p1, p2, entity as PolylineEntity)
      case EntityType.Arc:
        return TrimUtils.segmentVsArc(p1, p2, entity as ArcEntity)
      case EntityType.Circle:
        return TrimUtils.segmentVsCircle(p1, p2, entity as CircleEntity)
      case EntityType.Ellipse:
        return TrimUtils.segmentVsEllipse(p1, p2, entity as EllipseEntity)
      case EntityType.Spline:
        return TrimUtils.segmentVsSpline(p1, p2, entity as SplineEntity)
      case EntityType.Ray:
        return TrimUtils.segmentVsRay(p1, p2, entity as RayEntity)
      default:
        return []
    }
  }

  /** 段 vs 折线（精确，每段分直线/弧两种情况） */
  private static segmentVsPolyline(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    poly: PolylineEntity
  ): { x: number; y: number }[] {
    const result: { x: number; y: number }[] = []
    const vertices = poly.vertices
    const n = poly.isClosed ? vertices.length : vertices.length - 1
    for (let i = 0; i < n; i++) {
      const v1 = vertices[i]
      const v2 = vertices[(i + 1) % vertices.length]
      const bulge = v1.bulge ?? 0
      if (Math.abs(bulge) < 1e-10) {
        const pt = TrimUtils.segmentSegmentIntersection(p1, p2, v1, v2)
        if (pt) result.push(pt)
      } else {
        const arcPts = TrimUtils.sampleBulgeSegment(v1, v2, bulge, 16)
        for (let j = 0; j < arcPts.length - 1; j++) {
          const pt = TrimUtils.segmentSegmentIntersection(p1, p2, arcPts[j], arcPts[j + 1])
          if (pt) result.push(pt)
        }
      }
    }
    return result
  }

  /** 段 vs 圆弧（精确求交后过滤圆弧角度范围） */
  private static segmentVsArc(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    arc: ArcEntity
  ): { x: number; y: number }[] {
    const all = TrimUtils.segmentVsFullCircle(p1, p2, arc.center, arc.radius)
    const arcStart = TrimUtils.normalizeAngle(arc.startAngle)
    const arcEnd = TrimUtils.normalizeAngle(arc.endAngle)
    return all.filter((pt) => {
      const angle = TrimUtils.normalizeAngle(Math.atan2(pt.y - arc.center.y, pt.x - arc.center.x))
      return TrimUtils.isAngleOnDirectedArc(angle, arcStart, arcEnd, arc.clockwise)
    })
  }

  /** 段 vs 圆（精确） */
  private static segmentVsCircle(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    circle: CircleEntity
  ): { x: number; y: number }[] {
    return TrimUtils.segmentVsFullCircle(p1, p2, circle.center, circle.radius)
  }

  /**
   * 直线段与完整圆的精确交点。
   * 返回在段 [p1, p2] 范围内的交点（0、1 或 2 个）。
   */
  private static segmentVsFullCircle(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    center: { x: number; y: number },
    radius: number
  ): { x: number; y: number }[] {
    const dx = p2.x - p1.x,
      dy = p2.y - p1.y
    const fx = p1.x - center.x,
      fy = p1.y - center.y
    const a = dx * dx + dy * dy
    if (a < EPS * EPS) return []
    const b = 2 * (fx * dx + fy * dy)
    const c = fx * fx + fy * fy - radius * radius
    const disc = b * b - 4 * a * c
    if (disc < 0) return []
    const sq = Math.sqrt(Math.max(0, disc))
    const result: { x: number; y: number }[] = []
    for (const sign of [-1, 1]) {
      const t = (-b + sign * sq) / (2 * a)
      if (t >= -EPS && t <= 1 + EPS) {
        result.push({ x: p1.x + t * dx, y: p1.y + t * dy })
      }
    }
    return result
  }

  /** 段 vs 椭圆（变换到局部系后与单位圆求交，再反变换回世界坐标） */
  private static segmentVsEllipse(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    ellipse: EllipseEntity
  ): { x: number; y: number }[] {
    const { center, radiusX, radiusY, rotation, startAngle, endAngle } = ellipse
    const cosR = Math.cos(-rotation),
      sinR = Math.sin(-rotation)
    // 变换到局部（单位圆）坐标系
    const toLocal = (p: { x: number; y: number }) => {
      const dx = p.x - center.x,
        dy = p.y - center.y
      return { x: (dx * cosR - dy * sinR) / radiusX, y: (dx * sinR + dy * cosR) / radiusY }
    }
    const lp1 = toLocal(p1),
      lp2 = toLocal(p2)
    const unitPts = TrimUtils.segmentVsFullCircle(lp1, lp2, { x: 0, y: 0 }, 1)
    // 反变换回世界坐标并过滤椭圆弧角度范围
    return unitPts
      .map((lp) => {
        const wx =
          center.x + (lp.x * radiusX * Math.cos(rotation) - lp.y * radiusY * Math.sin(rotation))
        const wy =
          center.y + (lp.x * radiusX * Math.sin(rotation) + lp.y * radiusY * Math.cos(rotation))
        return { x: wx, y: wy }
      })
      .filter((pt) => {
        const dx = pt.x - center.x,
          dy = pt.y - center.y
        const lx = dx * Math.cos(rotation) + dy * Math.sin(rotation)
        const ly = -dx * Math.sin(rotation) + dy * Math.cos(rotation)
        const angle = TrimUtils.normalizeAngle(Math.atan2(ly / radiusY, lx / radiusX))
        const fullEllipse = TrimUtils.ccwSpan(startAngle, endAngle) < ANGLE_EPS
        if (fullEllipse) return true
        return TrimUtils.isAngleOnArc(angle, startAngle, endAngle)
      })
  }

  /** 段 vs 样条（采样后段-段求交） */
  private static segmentVsSpline(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    spline: SplineEntity
  ): { x: number; y: number }[] {
    const pts = TrimUtils.sampleSplineToPoints(spline, BOUNDARY_SAMPLE_NUM)
    const result: { x: number; y: number }[] = []
    for (let i = 0; i < pts.length - 1; i++) {
      const pt = TrimUtils.segmentSegmentIntersection(p1, p2, pts[i], pts[i + 1])
      if (pt) result.push(pt)
    }
    return result
  }

  /**
   * 段 vs 射线（精确求交，要求交点满足 t ≥ 0 沿射线方向）
   * 射线：startPoint + t * unitVector，t ≥ 0
   */
  private static segmentVsRay(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    ray: RayEntity
  ): { x: number; y: number }[] {
    const { startPoint, unitVector } = ray
    // 将 [p1,p2] 与射线直线求交，同时检查 t ≥ 0
    const dx = p2.x - p1.x,
      dy = p2.y - p1.y
    const ox = p1.x - startPoint.x,
      oy = p1.y - startPoint.y
    // det = uv × (p2-p1)
    const det = unitVector.x * dy - unitVector.y * dx
    if (Math.abs(det) < 1e-12) return [] // 平行
    // t_ray = (ox * dy - oy * dx) / det
    const tRay = (ox * dy - oy * dx) / det
    if (tRay < -EPS) return [] // 交点在射线起点背后
    // s_seg = (ox * uv.y - oy * uv.x) / det  (段参数)
    const sSeg = (ox * unitVector.y - oy * unitVector.x) / det
    if (sSeg < -EPS || sSeg > 1 + EPS) return [] // 交点不在段上
    return [
      {
        x: startPoint.x + tRay * unitVector.x,
        y: startPoint.y + tRay * unitVector.y,
      },
    ]
  }

  /** 两线段精确求交（Cramer's rule） */
  private static segmentSegmentIntersection(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number },
    p4: { x: number; y: number }
  ): { x: number; y: number } | null {
    const d1x = p2.x - p1.x,
      d1y = p2.y - p1.y
    const d2x = p4.x - p3.x,
      d2y = p4.y - p3.y
    const cross = d1x * d2y - d1y * d2x
    if (Math.abs(cross) < EPS) return null // 平行
    const dx = p3.x - p1.x,
      dy = p3.y - p1.y
    const t = (dx * d2y - dy * d2x) / cross
    const u = (dx * d1y - dy * d1x) / cross
    if (t >= -EPS && t <= 1 + EPS && u >= -EPS && u <= 1 + EPS) {
      return { x: p1.x + t * d1x, y: p1.y + t * d1y }
    }
    return null
  }

  // =========================================================================
  // 目标实体参数投影
  // =========================================================================

  private static projectOnPolyline(p: { x: number; y: number }, entity: PolylineEntity): number {
    const vertices = entity.vertices
    const n = entity.isClosed ? vertices.length : vertices.length - 1
    let bestT = 0,
      bestDistSq = Infinity
    for (let i = 0; i < n; i++) {
      const v1 = vertices[i]
      const v2 = vertices[(i + 1) % vertices.length]
      const bulge = v1.bulge ?? 0
      let t: number, point: { x: number; y: number }
      if (Math.abs(bulge) < 1e-10) {
        const dx = v2.x - v1.x,
          dy = v2.y - v1.y
        const lenSq = dx * dx + dy * dy
        if (lenSq < 1e-12) {
          t = 0
          point = { x: v1.x, y: v1.y }
        } else {
          const raw = ((p.x - v1.x) * dx + (p.y - v1.y) * dy) / lenSq
          t = Math.max(0, Math.min(1, raw))
          point = { x: v1.x + t * dx, y: v1.y + t * dy }
        }
      } else {
        const { cx, cy, radius, startAngle } = TrimUtils.arcParamsFromBulge(v1, v2, bulge)
        const sweep = 4 * Math.atan(bulge)
        let delta = Math.atan2(p.y - cy, p.x - cx) - startAngle
        if (sweep > 0) {
          while (delta < 0) delta += Math.PI * 2
          while (delta > Math.PI * 2) delta -= Math.PI * 2
        } else {
          while (delta > 0) delta -= Math.PI * 2
          while (delta < -Math.PI * 2) delta += Math.PI * 2
        }
        t = Math.max(0, Math.min(1, delta / sweep))
        const cAngle = startAngle + t * sweep
        point = { x: cx + radius * Math.cos(cAngle), y: cy + radius * Math.sin(cAngle) }
      }
      const ddx = p.x - point.x,
        ddy = p.y - point.y
      const distSq = ddx * ddx + ddy * ddy
      if (distSq < bestDistSq) {
        bestDistSq = distSq
        bestT = i + t
      }
    }
    return bestT
  }

  /**
   * 圆弧参数：t = ccwSpan(startAngle, projectedAngle) ∈ [0, arcSpan]
   */
  private static projectOnArc(p: { x: number; y: number }, entity: ArcEntity): number {
    const arcStart = TrimUtils.normalizeAngle(entity.startAngle)
    const arcEnd = TrimUtils.normalizeAngle(entity.endAngle)
    const totalSpan = TrimUtils.directedSpan(arcStart, arcEnd, entity.clockwise) || Math.PI * 2
    const angle = TrimUtils.normalizeAngle(Math.atan2(p.y - entity.center.y, p.x - entity.center.x))
    if (TrimUtils.isAngleOnDirectedArc(angle, arcStart, arcEnd, entity.clockwise)) {
      return TrimUtils.directedSpan(arcStart, angle, entity.clockwise)
    }
    // 夹紧到端点
    const dStart = TrimUtils.directedSpan(arcStart, angle, entity.clockwise)
    return dStart <= totalSpan / 2 ? 0 : totalSpan
  }

  /**
   * 圆参数：t = normalizedAngle ∈ [0, 2π)
   */
  private static projectOnCircle(p: { x: number; y: number }, entity: CircleEntity): number {
    return TrimUtils.normalizeAngle(Math.atan2(p.y - entity.center.y, p.x - entity.center.x))
  }

  /**
   * 椭圆参数：t = ccwSpan(startAngle, projectedLocalAngle)
   */
  private static projectOnEllipse(p: { x: number; y: number }, entity: EllipseEntity): number {
    const { center, radiusX, radiusY, rotation, startAngle, endAngle } = entity
    const dx = p.x - center.x,
      dy = p.y - center.y
    const cosR = Math.cos(rotation),
      sinR = Math.sin(rotation)
    const u = dx * cosR + dy * sinR
    const v = -dx * sinR + dy * cosR
    const angle = TrimUtils.normalizeAngle(Math.atan2(v / radiusY, u / radiusX))
    const fullEllipse = TrimUtils.ccwSpan(startAngle, endAngle) < ANGLE_EPS
    if (fullEllipse) return angle // 完整椭圆直接返回角度
    const totalSpan = TrimUtils.ccwSpan(startAngle, endAngle)
    const startN = TrimUtils.normalizeAngle(startAngle)
    if (TrimUtils.isAngleOnArc(angle, startN, TrimUtils.normalizeAngle(endAngle))) {
      return TrimUtils.ccwSpan(startN, angle)
    }
    const dStart = TrimUtils.ccwSpan(startN, angle)
    return dStart <= totalSpan / 2 ? 0 : totalSpan
  }

  private static projectOnSpline(p: { x: number; y: number }, entity: SplineEntity): number {
    if (entity.controlPoints.length < entity.degree + 1) return 0
    const { knots, degree } = entity
    const uMin = knots[degree] ?? 0
    const uMax = (knots[knots.length - degree - 1] ?? 1) - 1e-9
    let bestU = uMin,
      bestDistSq = Infinity
    for (let i = 0; i <= SPLINE_PROJECT_SAMPLE_NUM; i++) {
      const u = uMin + (uMax - uMin) * (i / SPLINE_PROJECT_SAMPLE_NUM)
      const pt = SplineUtils.evaluateBSpline(u, entity.controlPoints, entity.knots, entity.degree)
      const ddx = p.x - pt.x,
        ddy = p.y - pt.y
      const distSq = ddx * ddx + ddy * ddy
      if (distSq < bestDistSq) {
        bestDistSq = distSq
        bestU = u
      }
    }
    return bestU
  }

  /**
   * 射线参数：t = dot(p - startPoint, unitVector)，夹紧到 ≥ 0
   */
  private static projectOnRay(p: { x: number; y: number }, entity: RayEntity): number {
    const { startPoint, unitVector } = entity
    return Math.max(0, (p.x - startPoint.x) * unitVector.x + (p.y - startPoint.y) * unitVector.y)
  }

  // =========================================================================
  // 参数值 → 世界坐标
  // =========================================================================

  /**
   * 根据参数 t 计算实体上的点。
   *
   * @param entity - 目标实体
   * @param t - 参数值，范围与 projectOnEntity 的定义一致
   * @returns 对应的世界坐标点
   */
  static getPointAtParam(entity: IEntity, t: number): { x: number; y: number } {
    switch (entity.type) {
      case EntityType.Polyline:
        return TrimUtils.polylinePointAt(entity as PolylineEntity, t)
      case EntityType.Arc:
        return TrimUtils.arcPointAt(entity as ArcEntity, t)
      case EntityType.Circle:
        return TrimUtils.circlePointAt(entity as CircleEntity, t)
      case EntityType.Ellipse:
        return TrimUtils.ellipsePointAt(entity as EllipseEntity, t)
      case EntityType.Spline:
        return TrimUtils.splinePointAt(entity as SplineEntity, t)
      case EntityType.Ray:
        return TrimUtils.rayPointAt(entity as RayEntity, t)
      default:
        return { x: 0, y: 0 }
    }
  }

  private static polylinePointAt(entity: PolylineEntity, t: number): { x: number; y: number } {
    const vertices = entity.vertices
    const n = entity.isClosed ? vertices.length : vertices.length - 1
    const i = Math.max(0, Math.min(Math.floor(t), n - 1))
    const frac = Math.max(0, Math.min(1, t - i))
    const v1 = vertices[i]
    const v2 = vertices[(i + 1) % vertices.length]
    const bulge = v1.bulge ?? 0
    if (Math.abs(bulge) < 1e-10) {
      return { x: v1.x + (v2.x - v1.x) * frac, y: v1.y + (v2.y - v1.y) * frac }
    }
    const { cx, cy, radius, startAngle } = TrimUtils.arcParamsFromBulge(v1, v2, bulge)
    const sweep = 4 * Math.atan(bulge)
    const angle = startAngle + frac * sweep
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) }
  }

  /** 圆弧：t = ccwSpan(startAngle, angle)，angle = startAngle + t */
  private static arcPointAt(entity: ArcEntity, t: number): { x: number; y: number } {
    const startAngle = TrimUtils.normalizeAngle(entity.startAngle)
    const angle = entity.clockwise ? startAngle - t : startAngle + t
    return {
      x: entity.center.x + entity.radius * Math.cos(angle),
      y: entity.center.y + entity.radius * Math.sin(angle),
    }
  }

  /** 圆：t = normalizedAngle */
  private static circlePointAt(entity: CircleEntity, t: number): { x: number; y: number } {
    return {
      x: entity.center.x + entity.radius * Math.cos(t),
      y: entity.center.y + entity.radius * Math.sin(t),
    }
  }

  /** 椭圆：t = ccwSpan(startAngle, angle)，angle = startAngle + t */
  private static ellipsePointAt(entity: EllipseEntity, t: number): { x: number; y: number } {
    const { center, radiusX, radiusY, rotation, startAngle } = entity
    const fullEllipse = TrimUtils.ccwSpan(startAngle, entity.endAngle) < ANGLE_EPS
    const angle = fullEllipse ? t : TrimUtils.normalizeAngle(startAngle) + t
    const lx = radiusX * Math.cos(angle)
    const ly = radiusY * Math.sin(angle)
    return {
      x: center.x + lx * Math.cos(rotation) - ly * Math.sin(rotation),
      y: center.y + lx * Math.sin(rotation) + ly * Math.cos(rotation),
    }
  }

  private static splinePointAt(entity: SplineEntity, u: number): { x: number; y: number } {
    const pt = SplineUtils.evaluateBSpline(u, entity.controlPoints, entity.knots, entity.degree)
    return { x: pt.x, y: pt.y }
  }

  /** 射线参数：t = 距离 startPoint 的有向距离 */
  private static rayPointAt(entity: RayEntity, t: number): { x: number; y: number } {
    return {
      x: entity.startPoint.x + t * entity.unitVector.x,
      y: entity.startPoint.y + t * entity.unitVector.y,
    }
  }

  // =========================================================================
  // 参数范围
  // =========================================================================

  private static isEntityClosed(entity: IEntity): boolean {
    switch (entity.type) {
      case EntityType.Circle:
        return true
      case EntityType.Polyline:
        return (entity as PolylineEntity).isClosed
      case EntityType.Ellipse: {
        const e = entity as EllipseEntity
        return TrimUtils.ccwSpan(e.startAngle, e.endAngle) < ANGLE_EPS
      }
      default:
        return false
    }
  }

  /** 获取实体参数范围及周期（闭合实体） */
  private static getParamRange(entity: IEntity): { tMin: number; tMax: number; period?: number } {
    switch (entity.type) {
      case EntityType.Polyline: {
        const poly = entity as PolylineEntity
        const n = poly.isClosed ? poly.vertices.length : poly.vertices.length - 1
        return { tMin: 0, tMax: n, period: poly.isClosed ? n : undefined }
      }
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        const span =
          TrimUtils.directedSpan(arc.startAngle, arc.endAngle, arc.clockwise) || Math.PI * 2
        return { tMin: 0, tMax: span }
      }
      case EntityType.Circle:
        return { tMin: 0, tMax: Math.PI * 2, period: Math.PI * 2 }
      case EntityType.Ellipse: {
        const e = entity as EllipseEntity
        const span = TrimUtils.ccwSpan(e.startAngle, e.endAngle)
        if (span < ANGLE_EPS) {
          // 完整椭圆：以角度为周期
          return { tMin: 0, tMax: Math.PI * 2, period: Math.PI * 2 }
        }
        return { tMin: 0, tMax: span }
      }
      case EntityType.Spline: {
        const spline = entity as SplineEntity
        const uMin = spline.knots[spline.degree] ?? 0
        const uMax = (spline.knots[spline.knots.length - spline.degree - 1] ?? 1) - 1e-9
        return { tMin: uMin, tMax: uMax }
      }
      case EntityType.Ray:
        // 射线在 [0, ∞)，用足够大的值代表远端
        return { tMin: 0, tMax: RAY_SAMPLE_LENGTH }
      default:
        return { tMin: 0, tMax: 1 }
    }
  }

  // =========================================================================
  // 修剪区间查找
  // =========================================================================

  private static trimPolylineCursorSegment(
    entity: PolylineEntity,
    cursorPoint: { x: number; y: number },
    sorted: number[]
  ): BreakResult {
    const interval = TrimUtils.findPolylineCursorInterval(entity, cursorPoint, sorted)
    if (!interval || interval.tHi - interval.tLo < EPS) {
      return { createdEntities: [], deleteEntities: [] }
    }
    if (entity.isClosed) {
      return TrimUtils.trimClosedPolylineInterval(entity, interval.tLo, interval.tHi)
    }

    const p1 = TrimUtils.getPointAtParam(entity, interval.tLo)
    const p2 = TrimUtils.getPointAtParam(entity, interval.tHi)
    const res = BreakUtils.breakEntity(entity, { firstPoint: p1, secondPoint: p2 })
    if (res.createdEntities.length === 0 && res.deleteEntities.length === 0) {
      return { createdEntities: [], deleteEntities: [entity] }
    }
    return res
  }

  private static findTrimInterval(
    entity: IEntity,
    cursorPoint: { x: number; y: number },
    sorted: number[]
  ): TrimInterval | null {
    if (entity.type === EntityType.Polyline) {
      return TrimUtils.findPolylineCursorInterval(entity as PolylineEntity, cursorPoint, sorted)
    }

    const paramRange = TrimUtils.getParamRange(entity)
    if (sorted.length === 0) {
      return {
        tLo: paramRange.tMin,
        tHi: paramRange.tMax,
        wrapping: false,
      }
    }

    const cursorT = TrimUtils.projectOnEntity(entity, cursorPoint)
    if (TrimUtils.isEntityClosed(entity)) {
      return TrimUtils.findIntervalClosed(sorted, cursorT, paramRange.period!, entity, cursorPoint)
    }
    return TrimUtils.findIntervalOpen(sorted, cursorT, paramRange.tMin, paramRange.tMax)
  }

  private static findPolylineCursorInterval(
    entity: PolylineEntity,
    cursorPoint: { x: number; y: number },
    sorted: number[]
  ): TrimInterval | null {
    const cursorT = TrimUtils.projectOnPolyline(cursorPoint, entity)
    const maxSegmentIndex = entity.isClosed
      ? entity.vertices.length - 1
      : entity.vertices.length - 2
    const segmentIndex = Math.max(0, Math.min(Math.floor(cursorT), maxSegmentIndex))
    const tMin = segmentIndex
    const tMax = segmentIndex + 1
    const segmentTs = sorted.filter((t) => t >= tMin - EPS && t <= tMax + EPS)

    if (segmentTs.length > 0) {
      return TrimUtils.findIntervalOpen(segmentTs, cursorT, tMin, tMax)
    }
    return { tLo: tMin, tHi: tMax, wrapping: false }
  }

  private static createTrimmedIntervalEntities(entity: IEntity, interval: TrimInterval): IEntity[] {
    switch (entity.type) {
      case EntityType.Polyline:
        return TrimUtils.createPolylineTrimmedIntervalEntities(entity as PolylineEntity, interval)
      case EntityType.Arc:
        return TrimUtils.createArcTrimmedIntervalEntity(entity as ArcEntity, interval)
      case EntityType.Circle:
        return TrimUtils.createCircleTrimmedIntervalEntity(entity as CircleEntity, interval)
      case EntityType.Ellipse:
        return TrimUtils.createEllipseTrimmedIntervalEntity(entity as EllipseEntity, interval)
      case EntityType.Spline:
        return TrimUtils.createSplineTrimmedIntervalEntity(entity as SplineEntity, interval)
      case EntityType.Ray:
        return TrimUtils.createRayTrimmedIntervalEntity(entity as RayEntity, interval)
      default:
        return []
    }
  }

  private static createPolylineTrimmedIntervalEntities(
    entity: PolylineEntity,
    interval: TrimInterval
  ): IEntity[] {
    if (!entity.isClosed) {
      const result = BreakUtils.clipOpenPolylineToInterval(entity, interval.tLo, interval.tHi)
      return result ? [result] : []
    }

    if (!interval.wrapping) {
      const result = BreakUtils.clipClosedPolylineToInterval(entity, interval.tLo, interval.tHi)
      return result ? [result] : []
    }

    const maxT = entity.vertices.length
    return [
      BreakUtils.clipClosedPolylineToInterval(entity, interval.tLo, maxT),
      BreakUtils.clipClosedPolylineToInterval(entity, 0, interval.tHi),
    ].filter((item): item is PolylineEntity => Boolean(item))
  }

  private static createArcTrimmedIntervalEntity(
    entity: ArcEntity,
    interval: TrimInterval
  ): IEntity[] {
    if (interval.tHi - interval.tLo < ANGLE_EPS) return []
    const preview = entity.clone() as ArcEntity
    const direction = entity.clockwise ? -1 : 1
    Object.assign(preview, {
      id: nanoid(),
      startAngle: TrimUtils.normalizeAngle(entity.startAngle + direction * interval.tLo),
      endAngle: TrimUtils.normalizeAngle(entity.startAngle + direction * interval.tHi),
      clockwise: entity.clockwise,
    })
    return [preview]
  }

  private static createCircleTrimmedIntervalEntity(
    entity: CircleEntity,
    interval: TrimInterval
  ): IEntity[] {
    if (TrimUtils.ccwSpan(interval.tLo, interval.tHi) < ANGLE_EPS) return []
    return [
      new ArcEntity({
        ...entity.serialize(),
        id: nanoid(),
        type: EntityType.Arc,
        startAngle: TrimUtils.normalizeAngle(interval.tLo),
        endAngle: TrimUtils.normalizeAngle(interval.tHi),
        clockwise: false,
      }),
    ]
  }

  private static createEllipseTrimmedIntervalEntity(
    entity: EllipseEntity,
    interval: TrimInterval
  ): IEntity[] {
    const span = interval.wrapping
      ? TrimUtils.ccwSpan(interval.tLo, interval.tHi)
      : interval.tHi - interval.tLo
    if (span < ANGLE_EPS) return []
    const preview = entity.clone() as EllipseEntity
    const fullEllipse = TrimUtils.ccwSpan(entity.startAngle, entity.endAngle) < ANGLE_EPS
    const startAngle = fullEllipse ? interval.tLo : entity.startAngle + interval.tLo
    const endAngle = fullEllipse ? interval.tHi : entity.startAngle + interval.tHi
    Object.assign(preview, {
      id: nanoid(),
      startAngle: TrimUtils.normalizeAngle(startAngle),
      endAngle: TrimUtils.normalizeAngle(endAngle),
      clockwise: false,
    })
    return [preview]
  }

  private static createSplineTrimmedIntervalEntity(
    entity: SplineEntity,
    interval: TrimInterval
  ): IEntity[] {
    if (interval.tHi - interval.tLo < EPS) return []
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= 16; i++) {
      const u = interval.tLo + (interval.tHi - interval.tLo) * (i / 16)
      pts.push(SplineUtils.evaluateBSpline(u, entity.controlPoints, entity.knots, entity.degree))
    }
    try {
      const { controlPoints, knots, degree } = SplineUtils.fitPointsToSpline(pts)
      const preview = entity.clone() as SplineEntity
      Object.assign(preview, {
        id: nanoid(),
        method: 'FIT' as const,
        fitPoints: pts,
        controlPoints,
        knots,
        degree,
        isClosed: false,
        startTangent: undefined,
        endTangent: undefined,
      })
      return [preview]
    } catch {
      return []
    }
  }

  private static createRayTrimmedIntervalEntity(
    entity: RayEntity,
    interval: TrimInterval
  ): IEntity[] {
    if (interval.tHi - interval.tLo < EPS) return []
    return [
      new PolylineEntity({
        id: nanoid(),
        layerId: entity.layerId,
        colorMethod: entity.colorMethod,
        color: entity.color,
        lineType: entity.lineType,
        lineWidth: entity.lineWidth,
        vertices: [
          {
            x: entity.startPoint.x + interval.tLo * entity.unitVector.x,
            y: entity.startPoint.y + interval.tLo * entity.unitVector.y,
            bulge: 0,
          },
          {
            x: entity.startPoint.x + interval.tHi * entity.unitVector.x,
            y: entity.startPoint.y + interval.tHi * entity.unitVector.y,
            bulge: 0,
          },
        ],
        isClosed: false,
      }),
    ]
  }

  private static trimClosedPolylineInterval(
    entity: PolylineEntity,
    tLo: number,
    tHi: number
  ): BreakResult {
    const maxT = entity.vertices.length
    const keptParts: PolylineEntity[] = []
    if (tHi < maxT - EPS) {
      const part = BreakUtils.clipClosedPolylineToInterval(entity, tHi, maxT)
      if (part) keptParts.push(part)
    }
    if (tLo > EPS) {
      const part = BreakUtils.clipClosedPolylineToInterval(entity, 0, tLo)
      if (part) keptParts.push(part)
    }

    const keptVertices = keptParts.reduce<PolylineEntity['vertices']>((acc, part) => {
      if (acc.length === 0) return [...part.vertices]
      const last = acc[acc.length - 1]
      const first = part.vertices[0]
      if (Math.abs(last.x - first.x) < EPS && Math.abs(last.y - first.y) < EPS) {
        return [...acc.slice(0, -1), ...part.vertices]
      }
      return [...acc, ...part.vertices]
    }, [])

    if (keptVertices.length < 2) {
      return { createdEntities: [], deleteEntities: [entity] }
    }

    const newEntity = entity.clone() as PolylineEntity
    Object.assign(newEntity, {
      id: nanoid(),
      vertices: keptVertices,
      isClosed: false,
    })
    return { createdEntities: [newEntity], deleteEntities: [entity] }
  }

  /**
   * 开放实体：线性参数空间 [tMin, tMax]
   * 找到包含 cursorT 的区间，包含从端点到第一个/最后一个交点的情况
   */
  private static findIntervalOpen(
    sorted: number[],
    cursorT: number,
    tMin: number,
    tMax: number
  ): { tLo: number; tHi: number; wrapping: false } | null {
    if (sorted.length === 0) return null
    const firstT = sorted[0],
      lastT = sorted[sorted.length - 1]

    // 光标在所有交点之前：修剪从起点到第一个交点的段
    if (cursorT <= firstT + EPS) {
      if (firstT - tMin < EPS) return null // 没有可修剪的前段
      return { tLo: tMin, tHi: firstT, wrapping: false }
    }
    // 光标在所有交点之后：修剪从最后一个交点到终点的段
    if (cursorT >= lastT - EPS) {
      if (tMax - lastT < EPS) return null // 没有可修剪的后段
      return { tLo: lastT, tHi: tMax, wrapping: false }
    }
    // 光标在两个相邻交点之间
    for (let i = 0; i < sorted.length - 1; i++) {
      if (cursorT >= sorted[i] - EPS && cursorT <= sorted[i + 1] + EPS) {
        return { tLo: sorted[i], tHi: sorted[i + 1], wrapping: false }
      }
    }
    return null
  }

  /**
   * 闭合实体：循环参数空间，找到包含 cursorT 的圆弧段（含跨越周期的情况）
   * period：2π（圆/完整椭圆）或 n（闭合折线顶点数）
   */
  private static findIntervalClosed(
    sorted: number[],
    cursorT: number,
    period: number,
    entity?: IEntity,
    cursorPoint?: { x: number; y: number }
  ): { tLo: number; tHi: number; wrapping: boolean } | null {
    if (sorted.length < 2) return null
    const norm = (t: number) => ((t % period) + period) % period
    // CCW 跨度（周期内）
    const span = (from: number, to: number) => norm(to - from)
    const normalizedCursor = norm(cursorT)

    if (entity && cursorPoint) {
      const boundaryInterval = TrimUtils.findClosedBoundaryInterval(
        sorted,
        normalizedCursor,
        period,
        entity,
        cursorPoint
      )
      if (boundaryInterval) return boundaryInterval
    }

    for (let i = 0; i < sorted.length; i++) {
      const lo = norm(sorted[i])
      const hi = norm(sorted[(i + 1) % sorted.length])
      const intervalSpan = span(lo, hi)
      const cursorOffset = span(lo, normalizedCursor)
      if (cursorOffset <= intervalSpan + EPS) {
        const isWrapping = i === sorted.length - 1 // 最后一段跨越周期
        return { tLo: lo, tHi: hi, wrapping: isWrapping }
      }
    }
    return null
  }

  private static findClosedBoundaryInterval(
    sorted: number[],
    cursorT: number,
    period: number,
    entity: IEntity,
    cursorPoint: { x: number; y: number }
  ): { tLo: number; tHi: number; wrapping: boolean } | null {
    const norm = (t: number) => ((t % period) + period) % period
    const _span = (from: number, to: number) => norm(to - from)
    const circularDistance = (a: number, b: number) => {
      const delta = Math.abs(norm(a) - norm(b))
      return Math.min(delta, period - delta)
    }

    for (let i = 0; i < sorted.length; i++) {
      const current = norm(sorted[i])
      if (circularDistance(cursorT, current) > ANGLE_EPS) continue

      const prevIndex = (i - 1 + sorted.length) % sorted.length
      const nextIndex = (i + 1) % sorted.length
      const prev = norm(sorted[prevIndex])
      const next = norm(sorted[nextIndex])
      const before = {
        tLo: prev,
        tHi: current,
        wrapping: prevIndex > i,
      }
      const after = {
        tLo: current,
        tHi: next,
        wrapping: i === sorted.length - 1,
      }
      const beforeDistance = TrimUtils.distanceToClosedIntervalMidpoint(
        entity,
        cursorPoint,
        before.tLo,
        before.tHi,
        period
      )
      const afterDistance = TrimUtils.distanceToClosedIntervalMidpoint(
        entity,
        cursorPoint,
        after.tLo,
        after.tHi,
        period
      )
      return beforeDistance <= afterDistance ? before : after
    }
    return null
  }

  private static distanceToClosedIntervalMidpoint(
    entity: IEntity,
    point: { x: number; y: number },
    tLo: number,
    tHi: number,
    period: number
  ): number {
    const span = (((tHi - tLo) % period) + period) % period
    const midT = (tLo + span / 2) % period
    const midPoint = TrimUtils.getPointAtParam(entity, midT)
    return Math.hypot(point.x - midPoint.x, point.y - midPoint.y)
  }

  private static trimCircle(entity: CircleEntity, tLo: number, tHi: number): BreakResult {
    if (TrimUtils.ccwSpan(tLo, tHi) < ANGLE_EPS) {
      return { createdEntities: [], deleteEntities: [] }
    }
    return {
      createdEntities: [
        new ArcEntity({
          ...entity.serialize(),
          id: nanoid(),
          type: EntityType.Arc,
          startAngle: TrimUtils.normalizeAngle(tHi),
          endAngle: TrimUtils.normalizeAngle(tLo),
          clockwise: false,
        }),
      ],
      deleteEntities: [entity],
    }
  }

  private static trimFullEllipse(entity: EllipseEntity, tLo: number, tHi: number): BreakResult {
    if (TrimUtils.ccwSpan(tLo, tHi) < ANGLE_EPS) {
      return { createdEntities: [], deleteEntities: [] }
    }
    const newEntity = entity.clone() as EllipseEntity
    Object.assign(newEntity, {
      id: nanoid(),
      startAngle: TrimUtils.normalizeAngle(tHi),
      endAngle: TrimUtils.normalizeAngle(tLo),
    })
    return { createdEntities: [newEntity], deleteEntities: [entity] }
  }

  // =========================================================================
  // 辅助工具
  // =========================================================================

  private static deduplicateAndSort(arr: number[]): number[] {
    const sorted = [...arr].sort((a, b) => a - b)
    if (sorted.length === 0) return []
    const result = [sorted[0]]
    for (let i = 1; i < sorted.length; i++) {
      if (Math.abs(sorted[i] - result[result.length - 1]) > EPS) {
        result.push(sorted[i])
      }
    }
    return result
  }

  private static normalizeAngle(angle: number): number {
    const TWO_PI = Math.PI * 2
    return ((angle % TWO_PI) + TWO_PI) % TWO_PI
  }

  private static isAngleOnArc(angle: number, arcStart: number, arcEnd: number): boolean {
    const a = TrimUtils.normalizeAngle(angle)
    const s = TrimUtils.normalizeAngle(arcStart)
    const e = TrimUtils.normalizeAngle(arcEnd)
    if (s <= e) return a >= s - ANGLE_EPS && a <= e + ANGLE_EPS
    return a >= s - ANGLE_EPS || a <= e + ANGLE_EPS
  }

  private static ccwSpan(from: number, to: number): number {
    return TrimUtils.normalizeAngle(to - from)
  }

  private static directedSpan(from: number, to: number, clockwise: boolean): number {
    return clockwise ? TrimUtils.ccwSpan(to, from) : TrimUtils.ccwSpan(from, to)
  }

  private static isAngleOnDirectedArc(
    angle: number,
    arcStart: number,
    arcEnd: number,
    clockwise: boolean
  ): boolean {
    const sweep = TrimUtils.directedSpan(arcStart, arcEnd, clockwise) || Math.PI * 2
    const distanceFromStart = TrimUtils.directedSpan(arcStart, angle, clockwise)
    return distanceFromStart <= sweep + ANGLE_EPS
  }

  /**
   * 由两端点和凸度推算圆弧圆心、半径及起止角
   * bulge = tan(α/4)，α 为有向圆心角（CCW > 0）
   */
  private static arcParamsFromBulge(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    bulge: number
  ): { cx: number; cy: number; radius: number; startAngle: number; endAngle: number } {
    const dx = p2.x - p1.x,
      dy = p2.y - p1.y
    const L = Math.hypot(dx, dy)
    if (L < 1e-12) return { cx: p1.x, cy: p1.y, radius: 0, startAngle: 0, endAngle: 0 }
    const mx = (p1.x + p2.x) / 2,
      my = (p1.y + p2.y) / 2
    const nx = -dy / L,
      ny = dx / L
    const d_c = ((L / 2) * (1 - bulge * bulge)) / (2 * bulge)
    const cx = mx + nx * d_c,
      cy = my + ny * d_c
    const radius = Math.hypot(L / 2, d_c)
    return {
      cx,
      cy,
      radius,
      startAngle: Math.atan2(p1.y - cy, p1.x - cx),
      endAngle: Math.atan2(p2.y - cy, p2.x - cx),
    }
  }
}
